import { useEffect, useState } from "react";
import { Download } from "lucide-react";

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-AU", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit", hour12: true });
  } catch {
    return iso || "";
  }
};

const AdminRegistrations = ({ token, onUnauthorized, load, download }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    load(token)
      .then(setRows)
      .catch((err) => { if (err.response?.status === 401) onUnauthorized(); })
      .finally(() => setLoading(false));
  }, [token]);

  const exportCsv = async () => {
    setBusy(true);
    try {
      await download(token);
    } catch (err) {
      if (err.response?.status === 401) onUnauthorized();
    } finally {
      setBusy(false);
    }
  };

  const priority = rows.filter((r) => r.priority).length;

  return (
    <div className="mt-8" data-testid="admin-registrations">
      <div className="flex flex-wrap items-center justify-between gap-4 bg-cream border border-hairline/40 rounded-lg px-6 py-5">
        <p className="text-ink/80 text-sm" data-testid="registrations-count">
          <span className="font-serif text-deep-sea text-2xl mr-2">{rows.length}</span> registered ·{" "}
          <span className="font-serif text-deep-sea text-2xl mx-2">{priority}</span> on the priority appointment list
        </p>
        <button onClick={exportCsv} disabled={busy || rows.length === 0} data-testid="registrations-export-button"
          className="btn-gold inline-flex items-center gap-2 px-5 py-2.5 text-xs font-medium tracking-[0.08em] disabled:opacity-50">
          <Download className="w-3.5 h-3.5" strokeWidth={1.5} /> {busy ? "Preparing…" : "Download CSV"}
        </button>
      </div>

      {loading ? (
        <p className="text-ink/60 text-sm mt-8">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="text-ink/60 text-sm mt-8" data-testid="registrations-empty">No registrations yet.</p>
      ) : (
        <div className="overflow-x-auto mt-6 border border-hairline/40 rounded-lg">
          <table className="w-full text-sm" data-testid="registrations-table">
            <thead className="bg-cream text-left">
              <tr>
                {["Name", "Mobile", "Email", "Consent", "Priority", "Source", "Registered"].map((h) => (
                  <th key={h} className="px-4 py-3 text-[11px] tracking-[0.18em] uppercase text-soft-grey font-medium whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-hairline/30" data-testid={`registration-row-${r.id}`}>
                  <td className="px-4 py-3 text-deep-sea whitespace-nowrap">{r.first_name} {r.last_name}</td>
                  <td className="px-4 py-3 whitespace-nowrap">{r.phone}</td>
                  <td className="px-4 py-3">{r.email}</td>
                  <td className="px-4 py-3">{r.consent ? "Yes" : "No"}</td>
                  <td className="px-4 py-3">{r.priority ? "Yes" : "—"}</td>
                  <td className="px-4 py-3 text-ink/70">{[r.utm_source, r.utm_medium, r.utm_campaign].filter(Boolean).join(" · ") || "direct"}</td>
                  <td className="px-4 py-3 text-ink/70 whitespace-nowrap">{fmt(r.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminRegistrations;
